import { useEffect, useLayoutEffect, useState } from "react";

// material-ui
import { useTheme } from "@mui/material/styles";
import { Box, Typography, useMediaQuery } from "@mui/material";

// project-imports
import NavGroup from "./NavGroup";
import menuItem from "menu-items";
import { Menu } from "menu-items/dashboard";
import { allTicketsItem } from "menu-items/tickets";
import { getWorkflowList } from "actions/workflow";
import { hasIncentiveAccess } from "utils/route-guard/IncentiveGuard";
import { TaskSquare } from "iconsax-react";

import { useSelector } from "store";
import useAuth from "hooks/useAuth";
import { isAdminRole } from "utils/roles";
import useConfig from "hooks/useConfig";
import { HORIZONTAL_MAX_ITEM } from "config";
import { MenuOrientation } from "config";

// Groups only admins / super admins may see (user accounts, store directory,
// workflow builder, violations). Everyone else sees Dashboard + Tickets.
const ADMIN_ONLY_GROUPS = ["group-administration", "group-store-operations"];

// Groups restricted to a specific role set (in addition to admins). Currently
// none — Traders Program and the incentive groups are per-user toggles, below.
const GROUP_ALLOWED_ROLES = {};

// Individual items restricted further than the group that holds them. HVC
// Config decides which invoices are flagged and how much staff are paid, so it
// is super-admin only even though the rest of Store Operations is open to any
// admin.
const ITEM_ALLOWED_ROLES = {
  "hvc-config": ["SUPER_ADMIN"],
};

// Traders Program, Store Incentives and Staff Incentives pages are shown per
// user via the Advanced Features toggles (not by role). item id → toggle; a
// group appears only while it has an enabled page.
const INCENTIVE_ITEM_SECTION = {
  "traders-dashboard": "tradersDashboard",
  "incentive-redemptions": "tradersRedemptions",
  "traders-rc-mapping": "tradersRcMapping",
  "traders-approvals": "tradersApprovals",
  "store-incentives-dashboard": "storeDashboard",
  "store-incentives-target-upload": "storeTargetUpload",
  "store-incentives-assigned-staffs": "storeAssignedStaffs",
  "staff-incentives-clusterwise-dashboard": "clusterDashboard",
  "staff-incentives-clusterwise-targets": "clusterTargets",
  "staff-incentives-slabwise-dashboard": "slabDashboard",
  "staff-incentives-slabwise-targets": "slabTargets",
};
const INCENTIVE_GROUPS = [
  "group-traders-program",
  "group-store-incentives",
  "group-clusterwise-incentives",
  "group-slabwise-incentives",
];

// Drop items the viewer's role may not see, leaving the group itself intact.
const filterItems = (groups, role) =>
  groups.map((g) => {
    if (!g?.children?.length) return g;
    const children = g.children.filter((c) => {
      const allowed = ITEM_ALLOWED_ROLES[c.id];
      return !allowed || allowed.includes(role);
    });
    return children.length === g.children.length ? g : { ...g, children };
  });

// ==============================|| DRAWER CONTENT - NAVIGATION ||============================== //

const Navigation = () => {
  const theme = useTheme();

  const downLG = useMediaQuery(theme.breakpoints.down("lg"));

  const { menuOrientation } = useConfig();
  const { drawerOpen } = useSelector((state) => state.menu);
  const { user } = useAuth();
  const isAdmin = isAdminRole(user?.role);

  const [selectedItems, setSelectedItems] = useState("");
  const [selectedLevel, setSelectedLevel] = useState(0);
  const [menuItems, setMenuItems] = useState({ items: [] });

  useEffect(() => {
    handlerMenuItem();
    injectWorkflowTickets();
    // eslint-disable-next-line
  }, []);

  // Each active workflow becomes its own top-level item under the Tickets group.
  // "All tickets" leads them only when there's more than one workflow to span —
  // with a single workflow it's the same list as that workflow's own page.
  const injectWorkflowTickets = async () => {
    try {
      const res = await getWorkflowList(true);
      const workflows = res?.data || [];
      const group = menuItem.items.find((g) => g.id === "group-tickets");
      if (!group) return;
      // static children other than "All tickets" stay; the workflow ones refresh
      const base = (group.children || []).filter(
        (c) => !c.id?.startsWith("tickets-wf-") && c.id !== allTicketsItem.id,
      );
      const workflowItems = workflows.map((wf) => ({
        id: `tickets-wf-${wf.workflowId}`,
        title: wf.name,
        type: "item",
        icon: TaskSquare,
        url: `/tickets/workflow/${wf.workflowId}`,
      }));
      group.children = [
        ...(workflows.length > 1 ? [allTicketsItem] : []),
        ...base,
        ...workflowItems,
      ];
      setMenuItems({ ...menuItem });
    } catch (e) {
      // leave Tickets as-is if workflows can't be loaded
    }
  };

  useLayoutEffect(() => {
    setMenuItems(menuItem);
    // eslint-disable-next-line
  }, [menuItem]);

  let getMenu = Menu();
  const handlerMenuItem = () => {
    const isFound = menuItem.items.some((element) => {
      if (element.id === "group-dashboard") {
        return true;
      }
      return false;
    });

    if (getMenu?.id !== undefined && !isFound) {
      menuItem.items.splice(0, 0, getMenu);
      setMenuItems(menuItem);
    }
  };

  const isHorizontal =
    menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

  // The accounts team is scoped to the Traders Program (plus Staff Incentives, below) — nothing else.
  const isAccountsTeam = user?.role === "ACCOUNTS_TEAM";

  // Group visibility: accounts team sees only Traders; admin-only groups need
  // admin; role-restricted groups need a matching role; everything else is
  // visible to all.
  const visibleGroups = isAccountsTeam
    ? menuItems.items.filter(
        (g) => INCENTIVE_GROUPS.includes(g.id),
      )
    : menuItems.items.filter((g) => {
        // Incentive groups are narrowed per page by the toggles, below.
        if (INCENTIVE_GROUPS.includes(g.id)) return true;
        if (ADMIN_ONLY_GROUPS.includes(g.id)) return isAdmin;
        const allowed = GROUP_ALLOWED_ROLES[g.id];
        if (allowed) return allowed.includes(user?.role);
        return true;
      });

  // …then drop any individual items the role may not see within those groups.
  // Incentive groups: each page shows only when its toggle is on, and a group
  // with no enabled page disappears entirely.
  const items = filterItems(visibleGroups, user?.role)
    .map((g) => {
      if (!INCENTIVE_GROUPS.includes(g.id)) return g;
      return {
        ...g,
        children: (g.children || []).filter((c) =>
          hasIncentiveAccess(user, INCENTIVE_ITEM_SECTION[c.id]),
        ),
      };
    })
    .filter((g) => !INCENTIVE_GROUPS.includes(g.id) || g.children.length);

  const lastItem = isHorizontal ? HORIZONTAL_MAX_ITEM : null;
  let lastItemIndex = items.length - 1;
  let remItems = [];
  let lastItemId;

  if (lastItem && lastItem < items.length) {
    lastItemId = items[lastItem - 1].id;
    lastItemIndex = lastItem - 1;
    remItems = items
      .slice(lastItem - 1, items.length)
      .map((item) => ({
        title: item.title,
        elements: item.children,
        icon: item.icon,
      }));
  }

  const navGroups = items.slice(0, lastItemIndex + 1).map((item) => {
    switch (item.type) {
      case "group":
        return (
          <NavGroup
            key={item.id}
            setSelectedItems={setSelectedItems}
            setSelectedLevel={setSelectedLevel}
            selectedLevel={selectedLevel}
            selectedItems={selectedItems}
            lastItem={lastItem}
            remItems={remItems}
            lastItemId={lastItemId}
            item={item}
          />
        );
      default:
        return (
          <Typography key={item.id} variant="h6" color="error" align="center">
            Fix - Navigation Group
          </Typography>
        );
    }
  });
  return (
    <Box
      sx={{
        pt: drawerOpen ? (isHorizontal ? 0 : 2) : 0,
        "& > ul:first-of-type": { mt: 0 },
        display: isHorizontal ? { xs: "block", lg: "flex" } : "block",
      }}
    >
      {navGroups}
    </Box>
  );
};

export default Navigation;
